/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { useState, useRef, useEffect } from 'react';
import type { StreamResponse } from '../../types';

export const createStreamDict = (
  streams: StreamResponse[],
): Record<string, StreamResponse> =>
  streams.reduce(
    (list, item) => ({
      ...list,
      [item.stream]: item,
    }),
    {},
  );

export const useUserSleeping = (delay = 300000): [boolean, () => void] => {
  const [isSleeping, setIsSleeping] = useState(false);
  const autoInterval = useRef<NodeJS.Timeout | number | null>(null);
  const setUserToSleep = (): void => setIsSleeping(() => true);
  const clearTimer = (): void => {
    if (autoInterval.current) {
      clearTimeout(autoInterval.current as NodeJS.Timeout);
    }
  };
  const resetAutoInterval = (): void => {
    clearTimer();
    autoInterval.current = setTimeout(setUserToSleep, delay);
    if (isSleeping) {
      setIsSleeping(() => false);
    }
  };

  useEffect(() => {
    // mount
    resetAutoInterval();
    // unmount
    return (): void => {
      clearTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [isSleeping, resetAutoInterval];
};

export const getGraphHeightInPx = (): number => {
  const graphHeightInPx = 0.1 * document.documentElement.clientHeight; // using 10% of the screen per graph to make it responsive
  if (graphHeightInPx < 90) {
    return 90; // minimum height of 90px
  }
  return graphHeightInPx;
};
